import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  Blocks,
  Filter,
  Layers3,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Target,
  Wrench,
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_OPTIONS = ['human', 'growth', 'brand', 'media', 'market', 'ops', 'infra'];
const STATUS_OPTIONS = ['planned', 'building', 'active', 'research'];
const PRIORITY_OPTIONS = ['baja', 'media', 'alta', 'estrategica'];
const MONETIZATION_OPTIONS = ['core', 'addon', 'internal', 'enterprise'];

const STATUS_LABELS = {
  planned: 'Planificada',
  building: 'Building',
  active: 'Activa',
  research: 'Research',
  mio: 'Mío',
};

const STATUS_STYLES = {
  planned: 'border border-white/10 text-text-muted bg-white/5',
  building: 'border border-amber-400/20 text-amber-200 bg-amber-500/10',
  active: 'border border-primary/25 text-primary bg-primary/10',
  research: 'border border-sky-400/20 text-sky-200 bg-sky-500/10',
  mio: 'border border-primary/25 text-primary bg-primary/10',
};

const CATEGORY_LABELS = {
  human: 'Human',
  growth: 'Growth',
  brand: 'Brand',
  media: 'Media',
  market: 'Market',
  ops: 'Ops',
  infra: 'Infra',
};

const MetricCard = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        <p className="mt-2 text-xs text-text-muted">{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const WorkspacePanelCard = ({ panel }) => (
  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`skill-center-workspace-panel-${panel.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Panel interno</p>
        <h4 className="mt-2 text-base font-semibold text-text-heading">{panel.title}</h4>
      </div>
      <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[panel.status] || STATUS_STYLES.planned}`} data-testid={`skill-center-workspace-panel-status-${panel.id}`}>
        {STATUS_LABELS[panel.status] || panel.status}
      </span>
    </div>
    <p className="mt-3 text-sm text-text-body" data-testid={`skill-center-workspace-panel-helper-${panel.id}`}>{panel.helper}</p>
    <div className="mt-4 flex items-end justify-between gap-3">
      <div>
        <p className="text-2xl font-semibold text-text-heading" data-testid={`skill-center-workspace-panel-count-${panel.id}`}>{panel.count}</p>
        <p className="text-xs text-text-muted">capacidades dentro de este frente</p>
      </div>
      <ArrowUpRight className="h-4 w-4 text-primary" />
    </div>
  </div>
);

const FeaturedSkillCard = ({ skill, onSetStatus, onSetPriority, savingId }) => (
  <div className="rounded-[28px] border border-border-subtle bg-[#161616]/90 p-5 shadow-[0_18px_40px_rgba(0,0,0,0.22)]" data-testid={`skill-center-featured-skill-${skill.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-[20px] border border-primary/20 bg-primary/10 text-sm font-semibold text-primary" data-testid={`skill-center-featured-rank-${skill.id}`}>
          {String(skill.rank).padStart(2, '0')}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted" data-testid={`skill-center-featured-category-${skill.id}`}>
              {CATEGORY_LABELS[skill.category] || skill.category}
            </span>
            <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[skill.plugin_state] || STATUS_STYLES.planned}`} data-testid={`skill-center-featured-state-${skill.id}`}>
              {STATUS_LABELS[skill.plugin_state] || skill.plugin_state}
            </span>
          </div>
          <h4 className="mt-3 text-xl font-semibold text-text-heading" data-testid={`skill-center-featured-name-${skill.id}`}>{skill.name}</h4>
          <p className="mt-2 text-sm text-text-body" data-testid={`skill-center-featured-description-${skill.id}`}>{skill.description}</p>
        </div>
      </div>
      <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-primary" data-testid={`skill-center-featured-score-${skill.id}`}>
        {skill.score_label}
      </div>
    </div>

    <div className="mt-5 grid gap-3 md:grid-cols-[1fr_1fr] xl:grid-cols-[1.1fr_1.1fr_0.8fr]">
      <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`skill-center-featured-value-${skill.id}`}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Valor de negocio</p>
        <p className="mt-2 text-sm text-text-body">{skill.business_value || 'Pendiente de definir.'}</p>
      </div>
      <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`skill-center-featured-next-${skill.id}`}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Siguiente acción</p>
        <p className="mt-2 text-sm text-text-body">{skill.next_action || 'Sin siguiente paso definido.'}</p>
      </div>
      <div className="space-y-3 rounded-[20px] border border-border-subtle bg-card/40 p-4">
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado</label>
          <select className="input-kinesthetic min-w-0" value={skill.status} onChange={(e) => onSetStatus(skill.id, e.target.value)} data-testid={`skill-center-featured-status-select-${skill.id}`}>
            {STATUS_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Prioridad</label>
          <select className="input-kinesthetic min-w-0" value={skill.priority} onChange={(e) => onSetPriority(skill.id, e.target.value)} data-testid={`skill-center-featured-priority-select-${skill.id}`}>
            {PRIORITY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
      </div>
    </div>

    {!!skill.use_cases?.length && (
      <div className="mt-4 flex flex-wrap gap-2" data-testid={`skill-center-featured-use-cases-${skill.id}`}>
        {skill.use_cases.map((item) => (
          <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">
            {item}
          </span>
        ))}
      </div>
    )}

    {savingId === skill.id && <p className="mt-3 text-xs text-primary" data-testid={`skill-center-featured-saving-${skill.id}`}>Guardando cambios...</p>}
  </div>
);

const CompactSkillCard = ({ skill, onSetStatus, savingId }) => (
  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`skill-card-${skill.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted" data-testid={`skill-category-${skill.id}`}>
            {CATEGORY_LABELS[skill.category] || skill.category}
          </span>
          <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[skill.status] || STATUS_STYLES.planned}`} data-testid={`skill-state-badge-${skill.id}`}>
            {STATUS_LABELS[skill.status] || skill.status}
          </span>
        </div>
        <h4 className="mt-3 text-lg font-semibold text-text-heading" data-testid={`skill-name-${skill.id}`}>{skill.name}</h4>
        <p className="mt-2 text-sm text-text-body" data-testid={`skill-description-${skill.id}`}>{skill.description}</p>
      </div>
      <div className="min-w-[155px]">
        <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado rápido</label>
        <select className="input-kinesthetic min-w-0" value={skill.status} onChange={(e) => onSetStatus(skill.id, e.target.value)} data-testid={`skill-status-${skill.id}`}>
          {STATUS_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>
    </div>

    <div className="mt-4 grid gap-3 md:grid-cols-3">
      <div className="rounded-[18px] border border-border-subtle bg-card/40 p-3" data-testid={`skill-business-value-${skill.id}`}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Valor</p>
        <p className="mt-2 text-xs text-text-body">{skill.business_value || 'Pendiente'}</p>
      </div>
      <div className="rounded-[18px] border border-border-subtle bg-card/40 p-3" data-testid={`skill-providers-${skill.id}`}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Providers</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(skill.linked_providers || []).slice(0, 4).map((item) => (
            <span key={item} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">
              {item}
            </span>
          ))}
        </div>
      </div>
      <div className="rounded-[18px] border border-border-subtle bg-card/40 p-3" data-testid={`skill-scores-${skill.id}`}>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Scores</p>
        <p className="mt-2 text-xs text-text-body">R {skill.readiness_score} · I {skill.impact_score} · M {skill.margin_score}</p>
      </div>
    </div>

    {savingId === skill.id && <p className="mt-3 text-xs text-primary" data-testid={`skill-saving-${skill.id}`}>Guardando cambios...</p>}
  </div>
);

export default function SkillCenterHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPanel, setSelectedPanel] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({
    key: '',
    name: '',
    category: 'growth',
    status: 'planned',
    priority: 'alta',
    monetization_mode: 'core',
    owner_agent: 'ADMIN',
    description: '',
    business_value: '',
    linked_providers: 'GPT-5.2',
    use_cases: 'growth,automatizacion',
    readiness_score: 25,
    impact_score: 75,
    margin_score: 70,
    next_action: '',
  });

  const loadHub = async () => {
    try {
      const res = await authFetch(`${API}/admin/skills-hub`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el Skill Center');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el Skill Center');
    }
  };

  useEffect(() => {
    loadHub();
  }, []);

  const strategicCount = useMemo(() => hub?.metrics?.strategic || 0, [hub]);

  const filteredSkills = useMemo(() => {
    const term = search.trim().toLowerCase();
    const panelRules = {
      go_to_market: (item) => item.status === 'active' && ['core', 'addon', 'enterprise'].includes(item.monetization_mode),
      strategic_pipeline: (item) => item.priority === 'estrategica' && ['planned', 'building'].includes(item.status),
      internal_ops: (item) => item.monetization_mode === 'internal',
      research: (item) => item.status === 'research',
    };

    return (hub?.skills || []).filter((item) => {
      const searchMatch = !term || [
        item.name,
        item.description,
        item.business_value,
        item.owner_agent,
        ...(item.linked_providers || []),
        ...(item.use_cases || []),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
      const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory;
      const statusMatch = selectedStatus === 'all' || item.status === selectedStatus;
      const panelMatch = selectedPanel === 'all' || (panelRules[selectedPanel] ? panelRules[selectedPanel](item) : true);
      return searchMatch && categoryMatch && statusMatch && panelMatch;
    });
  }, [hub, search, selectedCategory, selectedStatus, selectedPanel]);

  const createSkill = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        linked_providers: form.linked_providers.split(',').map((item) => item.trim()).filter(Boolean),
        use_cases: form.use_cases.split(',').map((item) => item.trim()).filter(Boolean),
        readiness_score: Number(form.readiness_score),
        impact_score: Number(form.impact_score),
        margin_score: Number(form.margin_score),
      };
      const res = await authFetch(`${API}/admin/skills-hub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la skill');
      toast.success('Skill añadida al ecosistema');
      setForm({
        key: '',
        name: '',
        category: 'growth',
        status: 'planned',
        priority: 'alta',
        monetization_mode: 'core',
        owner_agent: 'ADMIN',
        description: '',
        business_value: '',
        linked_providers: 'GPT-5.2',
        use_cases: 'growth,automatizacion',
        readiness_score: 25,
        impact_score: 75,
        margin_score: 70,
        next_action: '',
      });
      setShowCreateForm(false);
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo crear la skill');
    } finally {
      setSubmitting(false);
    }
  };

  const updateSkill = async (skillId, payload) => {
    try {
      setSavingId(skillId);
      const res = await authFetch(`${API}/admin/skills-hub/${skillId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar la skill');
      setHub((prev) => ({
        ...prev,
        skills: prev.skills.map((item) => (item.id === skillId ? data.skill : item)),
        featured_skills: (prev.featured_skills || []).map((item) => (item.id === skillId ? { ...item, ...data.skill } : item)),
      }));
      toast.success('Skill actualizada');
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la skill');
    } finally {
      setSavingId(null);
    }
  };

  if (!hub) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="skill-center-loading">Cargando Skill Center...</div>;
  }

  return (
    <div className="space-y-6" data-testid="skill-center-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="skill-center-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Skill Center</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · catálogo modular tipo tools/plugins</p>
            <h3 className="mt-4 max-w-4xl text-3xl font-semibold text-text-heading">Ahora el Skill Center se comporta como un catálogo vivo de herramientas: más rápido de leer, más fácil de priorizar y mucho más útil para vender y operar.</h3>
            <p className="mt-4 max-w-3xl text-sm text-text-body">La lógica ya existía. El cambio era visual y operativo: reducir fricción, convertir capacidades en módulos escaneables y dar contexto estratégico inmediato.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="skill-center-diagnosis-chips">
              <span className="insight-chip">Modularidad visible</span>
              <span className="insight-chip">Lectura rápida</span>
              <span className="insight-chip">Estados claros</span>
              <span className="insight-chip">Descubrimiento mejorado</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard icon={Layers3} label="Total" value={hub.metrics.total} helper="capacidades registradas" testId="skill-center-metric-total" />
            <MetricCard icon={Wrench} label="Activas" value={hub.metrics.active} helper="listas para operar" testId="skill-center-metric-active" />
            <MetricCard icon={Sparkles} label="Building" value={hub.metrics.building} helper="en construcción ahora" testId="skill-center-metric-building" />
            <MetricCard icon={Target} label="Estratégicas" value={strategicCount} helper="prioridad máxima" testId="skill-center-metric-strategic" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-4" data-testid="skill-center-workspace-panels-grid">
        {(hub.workspace_panels || []).map((panel) => (
          <button
            key={panel.id}
            type="button"
            onClick={() => setSelectedPanel((prev) => (prev === panel.id ? 'all' : panel.id))}
            className={`text-left transition-transform duration-200 hover:-translate-y-1 ${selectedPanel === panel.id ? 'ring-1 ring-primary/40' : ''}`}
            data-testid={`skill-center-workspace-panel-button-${panel.id}`}
          >
            <WorkspacePanelCard panel={panel} />
          </button>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="glass-card tracing-panel p-6" data-testid="skill-center-browser-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Plugin browser interno</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Descubre, filtra y prioriza capacidades sin perder tiempo</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={loadHub} className="btn-secondary" data-testid="skill-center-refresh-button">
                <RefreshCw className="h-4 w-4" /> Refrescar
              </button>
              <button type="button" onClick={() => setShowCreateForm((prev) => !prev)} className="btn-primary" data-testid="skill-center-toggle-create-button">
                <Plus className="h-4 w-4" /> {showCreateForm ? 'Cerrar alta' : 'Nueva skill'}
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr]">
            <div className="relative" data-testid="skill-center-search-wrapper">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input className="input-kinesthetic pl-11" placeholder="Buscar skill, provider o caso de uso" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="skill-center-search-input" />
            </div>
            <select className="input-kinesthetic" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} data-testid="skill-center-category-filter">
              <option value="all">Todas las categorías</option>
              {CATEGORY_OPTIONS.map((item) => <option key={item} value={item}>{CATEGORY_LABELS[item] || item}</option>)}
            </select>
            <select className="input-kinesthetic" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} data-testid="skill-center-status-filter">
              <option value="all">Todos los estados</option>
              {STATUS_OPTIONS.map((item) => <option key={item} value={item}>{STATUS_LABELS[item] || item}</option>)}
            </select>
            <button type="button" onClick={() => { setSearch(''); setSelectedCategory('all'); setSelectedStatus('all'); setSelectedPanel('all'); }} className="btn-secondary w-full" data-testid="skill-center-clear-filters-button">
              <Filter className="h-4 w-4" /> Limpiar filtros
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="skill-center-category-chip-list">
            <button type="button" onClick={() => setSelectedCategory('all')} className={`rounded-full border px-3 py-2 text-[11px] uppercase tracking-[0.16em] ${selectedCategory === 'all' ? 'border-primary/25 bg-primary/10 text-primary' : 'border-border-subtle text-text-muted'}`} data-testid="skill-center-category-chip-all">
              Todo
            </button>
            {(hub.category_breakdown || []).map((item) => (
              <button
                key={item.category}
                type="button"
                onClick={() => setSelectedCategory(item.category)}
                className={`rounded-full border px-3 py-2 text-[11px] uppercase tracking-[0.16em] ${selectedCategory === item.category ? 'border-primary/25 bg-primary/10 text-primary' : 'border-border-subtle text-text-muted'}`}
                data-testid={`skill-center-category-chip-${item.category}`}
              >
                {CATEGORY_LABELS[item.category] || item.category} · {item.count}
              </button>
            ))}
          </div>

          <div className="mt-5 rounded-[24px] border border-border-subtle bg-card/30 p-4" data-testid="skill-center-results-summary-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Resultado filtrado</p>
                <p className="mt-2 text-lg font-semibold text-text-heading" data-testid="skill-center-results-count">{filteredSkills.length} skills visibles</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="skill-center-status-breakdown">
                {(hub.status_breakdown || []).map((item) => (
                  <span key={item.status} className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || STATUS_STYLES.planned}`} data-testid={`skill-center-status-breakdown-${item.status}`}>
                    {STATUS_LABELS[item.status] || item.status} · {item.count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card tracing-panel p-6" data-testid="skill-center-create-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Alta rápida</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Registrar nueva capacidad</h4>
            </div>
            <Blocks className="h-5 w-5 text-primary" />
          </div>

          {showCreateForm ? (
            <form onSubmit={createSkill} className="mt-5 space-y-4" data-testid="skill-center-create-form">
              <div className="grid gap-4 md:grid-cols-2">
                <input className="input-kinesthetic" placeholder="Clave única" value={form.key} onChange={(e) => setForm((prev) => ({ ...prev, key: e.target.value }))} data-testid="skill-center-key-input" />
                <input className="input-kinesthetic" placeholder="Nombre de la skill" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} data-testid="skill-center-name-input" />
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <select className="input-kinesthetic" value={form.category} onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))} data-testid="skill-center-category-select">{CATEGORY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                <select className="input-kinesthetic" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))} data-testid="skill-center-status-select">{STATUS_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                <select className="input-kinesthetic" value={form.priority} onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))} data-testid="skill-center-priority-select">{PRIORITY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
                <select className="input-kinesthetic" value={form.monetization_mode} onChange={(e) => setForm((prev) => ({ ...prev, monetization_mode: e.target.value }))} data-testid="skill-center-monetization-select">{MONETIZATION_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}</select>
              </div>

              <input className="input-kinesthetic" placeholder="Agente owner" value={form.owner_agent} onChange={(e) => setForm((prev) => ({ ...prev, owner_agent: e.target.value.toUpperCase() }))} data-testid="skill-center-owner-input" />
              <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Descripción" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} data-testid="skill-center-description-input" />
              <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Valor de negocio" value={form.business_value} onChange={(e) => setForm((prev) => ({ ...prev, business_value: e.target.value }))} data-testid="skill-center-business-value-input" />
              <input className="input-kinesthetic" placeholder="Providers (coma)" value={form.linked_providers} onChange={(e) => setForm((prev) => ({ ...prev, linked_providers: e.target.value }))} data-testid="skill-center-providers-input" />
              <input className="input-kinesthetic" placeholder="Casos de uso (coma)" value={form.use_cases} onChange={(e) => setForm((prev) => ({ ...prev, use_cases: e.target.value }))} data-testid="skill-center-use-cases-input" />

              <div className="grid gap-4 md:grid-cols-3">
                <input type="number" className="input-kinesthetic" min="0" max="100" value={form.readiness_score} onChange={(e) => setForm((prev) => ({ ...prev, readiness_score: e.target.value }))} data-testid="skill-center-readiness-input" />
                <input type="number" className="input-kinesthetic" min="0" max="100" value={form.impact_score} onChange={(e) => setForm((prev) => ({ ...prev, impact_score: e.target.value }))} data-testid="skill-center-impact-input" />
                <input type="number" className="input-kinesthetic" min="0" max="100" value={form.margin_score} onChange={(e) => setForm((prev) => ({ ...prev, margin_score: e.target.value }))} data-testid="skill-center-margin-input" />
              </div>

              <input className="input-kinesthetic" placeholder="Siguiente acción" value={form.next_action} onChange={(e) => setForm((prev) => ({ ...prev, next_action: e.target.value }))} data-testid="skill-center-next-action-input" />
              <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="skill-center-submit-button">
                <Plus className="h-4 w-4" /> {submitting ? 'Guardando...' : 'Añadir skill'}
              </button>
            </form>
          ) : (
            <div className="mt-5 rounded-[24px] border border-dashed border-primary/25 bg-primary/5 p-6" data-testid="skill-center-create-placeholder">
              <p className="text-sm text-text-body">Activa “Nueva skill” para abrir el formulario. Así mantenemos la pantalla limpia y el catálogo visible, igual que una interfaz moderna de tools/plugins.</p>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4" data-testid="skill-center-featured-list">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Top capabilities</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Capacidades destacadas para dirección y venta</h4>
          </div>
          <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-primary" data-testid="skill-center-featured-count">
            {(hub.featured_skills || []).length} destacadas
          </span>
        </div>

        {(hub.featured_skills || []).map((skill) => (
          <FeaturedSkillCard
            key={skill.id}
            skill={skill}
            savingId={savingId}
            onSetStatus={(skillId, status) => updateSkill(skillId, { status })}
            onSetPriority={(skillId, priority) => updateSkill(skillId, { priority })}
          />
        ))}
      </div>

      <div className="space-y-4" data-testid="skill-center-list">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Catálogo completo</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Vista escaneable de todas las skills</h4>
          </div>
          {selectedPanel !== 'all' && (
            <span className="rounded-full border border-secondary/20 bg-secondary/10 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-secondary" data-testid="skill-center-active-panel-filter">
              Filtro de panel · {selectedPanel}
            </span>
          )}
        </div>

        {filteredSkills.length ? (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredSkills.map((skill) => (
              <CompactSkillCard key={skill.id} skill={skill} savingId={savingId} onSetStatus={(skillId, status) => updateSkill(skillId, { status })} />
            ))}
          </div>
        ) : (
          <div className="glass-card p-8 text-center text-text-muted" data-testid="skill-center-empty-state">
            No hay skills que coincidan con los filtros actuales.
          </div>
        )}
      </div>
    </div>
  );
}